import React, { useId } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import * as Colors from '~styles/colors';
import { useAnimateAfterDataChange } from '~styles/use-animate-after-data-change';
import { MOTION_DURATION_MS, MOTION_EASE_RECHARTS } from '~styles/motion';
import { resolveSingleSeriesColor } from './palette-colors';
import { AREA_STYLE_PROPS, AreaGradientDef, CHART_MARGIN } from './area-chart';
import type { KpiCardData, KpiFormProps } from '../types';

type LinePoints = Extract<KpiCardData, { form: 'line' }>['points'];

const LineCard = ({
    points,
    stroke,
    chartStyle,
}: {
    points: LinePoints;
    stroke: string;
    chartStyle?: 'area';
}) => {
    const animate = useAnimateAfterDataChange(
        points.map((point) => point.value).join(',')
    );
    // Colons from useId() are valid in an id but not in a CSS url(#...) selector.
    const gradientId = `line-area-${useId().replace(/:/g, '')}`;

    const axes = (
        <>
            <CartesianGrid
                strokeDasharray="4 4"
                stroke={Colors.UNKNOWN_CT_BLUE_OPACITY_15}
            />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickMargin={4} />
            <YAxis tick={{ fontSize: 10 }} width={28} allowDecimals={false} />
            <Tooltip />
        </>
    );

    return (
        <ResponsiveContainer width="100%" height={180}>
            {chartStyle === 'area' ? (
                <AreaChart data={points} margin={CHART_MARGIN}>
                    <defs>
                        <AreaGradientDef id={gradientId} color={stroke} />
                    </defs>
                    {axes}
                    <Area
                        {...AREA_STYLE_PROPS}
                        dataKey="value"
                        stroke={stroke}
                        fill={`url(#${gradientId})`}
                        isAnimationActive={animate}
                        animationDuration={MOTION_DURATION_MS}
                        animationEasing={MOTION_EASE_RECHARTS}
                        animationBegin={0}
                    />
                </AreaChart>
            ) : (
                <LineChart data={points} margin={CHART_MARGIN}>
                    {axes}
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke={stroke}
                        strokeWidth={2.5}
                        dot={{ r: 3, strokeWidth: 2, fill: Colors.WHITE }}
                        activeDot={{ r: 5 }}
                        isAnimationActive={animate}
                        animationDuration={MOTION_DURATION_MS}
                        animationEasing={MOTION_EASE_RECHARTS}
                        animationBegin={0}
                    />
                </LineChart>
            )}
        </ResponsiveContainer>
    );
};

/** Line form: a compact daily series. Card-sized (not the 428px full-page chart). */
export const LineForm: React.FC<KpiFormProps> = ({
    data,
    palette,
    chartStyle,
}) => {
    if (data.form !== 'line') return null;
    return (
        <LineCard
            points={data.points}
            stroke={resolveSingleSeriesColor(palette)}
            chartStyle={chartStyle}
        />
    );
};
